import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/AuthContext';
import LandingPage from './components/LandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TrustPage from './components/TrustPage';
import TermsOfService from './components/TermsOfService';
import { Sidebar, TopBar } from './components/DashboardLayout';
import StudentDashboard from './components/StudentDashboard';
import TeacherDashboard from './components/TeacherDashboard';
import Settings from './components/Settings';
import JoinClass from './components/JoinClass';
import AssignmentJoin from './components/AssignmentJoin';
import WriteSpace from './components/WriteSpace';
import RoleSelect from './components/RoleSelect';
import ErrorBoundary from './components/ErrorBoundary';
import FlowBar from './components/FlowBar';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, needsRoleSelection } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  // E007 safety net: if loading hangs for 12 seconds, clear everything and go to landing page
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      console.warn('ProtectedRoute: loading timeout — clearing session');
      localStorage.removeItem('draftflow_user');
      setTimedOut(true);
    }, 12000);
    return () => clearTimeout(timer);
  }, [loading]);

  if (timedOut || (!loading && !isAuthenticated)) {
    return <Navigate to="/" replace />;
  }

  // First sign-in with unknown role: ask before showing any dashboard
  if (!loading && needsRoleSelection) {
    return <RoleSelect />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-flow-blue border-t-transparent mx-auto mb-4" />
          <p className="text-slate-400">Loading DraftFlow...</p>
        </div>
      </div>
    );
  }

  return children;
};

// Dashboard Shell
const DashboardShell = () => {
  const { isTeacher, profile, isDemo, loading, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState(isTeacher ? 'dashboard' : 'assignments');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Order 4 (Q12): a data-loading view must never spin forever. If the profile
  // hasn't resolved in 8s, show a human-readable recovery screen, not a spinner.
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  useEffect(() => {
    if (!loading && profile) return;
    const t = setTimeout(() => setLoadTimedOut(true), 8000);
    return () => clearTimeout(t);
  }, [loading, profile]);

  const getTitle = () => {
    if (isTeacher) {
      switch (activeTab) {
        case 'dashboard': return 'Dashboard';
        case 'assignments': return 'Assignments';
        case 'analytics': return 'Insights';
        case 'rubric': return 'Rubrics';
        case 'classroom': return 'Classes';
        case 'settings': return 'Settings';
        default: return 'Dashboard';
      }
    } else {
      switch (activeTab) {
        case 'assignments': return 'Assignments';
        case 'settings': return 'Settings';
        default: return 'Assignments';
      }
    }
  };

  // Page-title eyebrows (PHASE-1.5-BRAND-SPEC.md §4): DraftBoard, Assignments,
  // Student assignment list. DraftSpace carries its own inside the editor header.
  const getEyebrow = () => {
    if (isTeacher) {
      if (activeTab === 'dashboard') return { label: 'TEACHER COMMAND CENTER', tone: 'gold' };
      if (activeTab === 'assignments') return { label: 'CREATE & ASSIGN', tone: 'blue' };
      return null;
    }
    if (activeTab === 'assignments') return { label: 'YOUR WRITING', tone: 'blue' };
    return null;
  };

  // Guard: Don't render dashboard until profile is resolved.
  // Prevents stale localStorage from briefly showing the wrong role's dashboard.
  if (loading || !profile) {
    if (loadTimedOut) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
          <div className="max-w-md w-full text-center">
            <h1 className="font-heading text-2xl font-bold text-white mb-2">We couldn't load your dashboard</h1>
            <p className="text-slate-400 mb-6">This usually clears up on a reload. If it keeps happening, sign in again.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => window.location.reload()} className="btn-teal px-6 py-2 rounded-lg" data-testid="dashboard-reload">Reload</button>
              <button onClick={signOut} className="border border-border-secondary text-cream hover:border-flow-blue-hover px-6 py-2 rounded-lg transition-colors" data-testid="dashboard-resignin">Sign in again</button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-flow-blue border-t-transparent mx-auto mb-4" />
          <p className="text-slate-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    // Student views
    if (!isTeacher) {
      switch (activeTab) {
        case 'settings':
          return <ErrorBoundary name="Settings"><Settings /></ErrorBoundary>;
        default:
          return <ErrorBoundary name="Assignments"><StudentDashboard /></ErrorBoundary>;
      }
    }
    // Teacher views
    switch (activeTab) {
      case 'settings':
        return <ErrorBoundary name="Settings"><Settings /></ErrorBoundary>;
      default:
        return <ErrorBoundary name="TeacherDashboard"><TeacherDashboard activeTab={activeTab} onTabChange={setActiveTab} /></ErrorBoundary>;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-slate-900 overflow-hidden">
      {/* FlowBar — app shell top edge, full-bleed (spec §2) */}
      <FlowBar height={4} />
      <div className="flex flex-1 overflow-hidden">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-flow-blue focus:text-white focus:px-4 focus:py-2 focus:rounded-lg">Skip to main content</a>
        <Sidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar
            title={getTitle()}
            subtitle={isTeacher ? 'Teacher View' : 'Student View'}
            eyebrow={getEyebrow()}
          />
          {isDemo && (
            <div className="bg-gold-bright/10 border-b border-gold-bright/30 text-gold-bright text-sm px-4 py-2 text-center">
              You're exploring DraftFlow in demo mode. Sign in with Google to access all features and save your work.
            </div>
          )}
          <main id="main-content" className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </main>
        </div>
      </div>
      {/* FlowBar — app footer bottom edge (spec §2) */}
      <FlowBar height={4} />
    </div>
  );
};

// Auth Callback Handler
const AuthCallback = () => {
  const { isAuthenticated, loading } = useAuth();

  // Detect an OAuth error return (e.g. the user cancelled on the Google
  // consent screen). Supabase reports it in the query string or URL hash.
  const [oauthError] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    return query.get('error') || hash.get('error') || null;
  });

  if (oauthError) {
    const cancelled = oauthError === 'access_denied';
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h1 className="font-heading text-2xl font-bold text-white mb-2">
            {cancelled ? 'Sign-in cancelled' : "We couldn't sign you in"}
          </h1>
          <p className="text-slate-400 mb-6">
            {cancelled
              ? 'No problem — nothing was set up. You can sign in whenever you are ready.'
              : 'Something interrupted the Google sign-in. Please try again.'}
          </p>
          <button
            onClick={() => { window.location.href = '/'; }}
            className="btn-teal px-6 py-2 rounded-lg"
            data-testid="oauth-error-home"
          >
            Back to DraftFlow
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-flow-blue border-t-transparent mx-auto mb-4" />
          <p className="text-slate-400">Completing sign in...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Honor a stored post-login destination (e.g. the assignment link the
    // student clicked in Google Classroom) instead of always landing on
    // the dashboard.
    const dest = sessionStorage.getItem('draftflow_post_login_redirect');
    if (dest && dest.startsWith('/')) {
      sessionStorage.removeItem('draftflow_post_login_redirect');
      return <Navigate to={dest} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/" replace />;
};

// OAuth popup landing — the Classroom/Drive consent popup (opened with
// noopener) is redirected by the backend to "/?gc_connected=true" etc.
// Without this gate the popup would just re-render the app with the param
// silently dropped, and the window that opened it would never learn the
// result. This relays the outcome over BroadcastChannel and closes the popup.
const OAUTH_RESULT_PARAMS = ['gc_connected', 'gc_error', 'drive_connected', 'drive_error'];

const OAuthPopupRelay = ({ children }) => {
  const [isOAuthReturn, setIsOAuthReturn] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return OAUTH_RESULT_PARAMS.some((p) => params.has(p));
  });

  useEffect(() => {
    if (!isOAuthReturn) return;
    const params = new URLSearchParams(window.location.search);
    let message = null;
    if (params.get('gc_connected') === 'true') message = 'gc_connected';
    else if (params.has('gc_error')) message = `gc_error:${params.get('gc_error')}`;
    else if (params.get('drive_connected') === 'true') message = 'drive_connected';
    else if (params.has('drive_error')) message = `drive_error:${params.get('drive_error')}`;

    if (message && typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('draftflow-oauth');
      channel.postMessage(message);
      channel.close();
    }
    // Best effort — browsers may refuse to close a noopener window
    window.close();
  }, [isOAuthReturn]);

  if (isOAuthReturn) {
    const params = new URLSearchParams(window.location.search);
    const failed = params.has('gc_error') || params.has('drive_error');
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-slate-800 rounded-xl p-8 max-w-md text-center">
          <h2 className="font-heading text-xl text-white mb-2">
            {failed ? 'Connection failed' : 'Google account connected!'}
          </h2>
          <p className="text-slate-400 mb-6">
            {failed
              ? `Something went wrong: ${params.get('gc_error') || params.get('drive_error')}. Close this window and try again.`
              : 'You can close this window and return to DraftFlow.'}
          </p>
          <button
            onClick={() => {
              window.history.replaceState({}, '', '/');
              setIsOAuthReturn(false);
            }}
            className="btn-teal px-6 py-2 rounded-lg"
          >
            Continue to DraftFlow
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Root App Component — E007 fix: never block public routes on auth loading.
// Only the /dashboard route (via ProtectedRoute) should wait for auth to resolve.
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  return (
    <Routes>
      <Route
        path="/"
        element={!loading && isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/join" element={<JoinClass />} />
      <Route path="/join/class/:joinCode" element={<JoinClass />} />
      <Route path="/join/assignment/:assignmentId" element={<AssignmentJoin />} />
      {/* Canonical short join link (Q11): /join/<code> */}
      <Route path="/join/:code" element={<JoinClass />} />
      <Route
        path="/write/:assignmentId"
        element={
          <ProtectedRoute>
            <ErrorBoundary name="WriteSpace">
              <WriteSpace />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardShell />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OAuthPopupRelay>
          <AppRoutes />
        </OAuthPopupRelay>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#161F30',
              border: '1px solid #334155',
              color: '#FAF7F2',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
